import type { RouteRecordRaw } from "vue-router";
import { createRouter, createWebHistory } from "vue-router";
import EquipmentRoutes from "./equipment";
import sizingsRoutes from "./sizings";
import quotesRoutes from "./quotes";
import Home from "@/views/Home.vue";
import EquipmentAdmin from "@/views/EquipmentAdmin.vue";
import User from "@/views/User.vue";
import Well from "@/views/Well.vue";
import Wells from "@/views/Wells.vue";
import CacheMiss from "@/views/CacheMiss.vue";
import LoggedOut from "@/views/LoggedOut.vue";
import DeletedSizings from "@/views/DeletedSizings.vue";
import PageNotFound from "@/views/PageNotFound.vue";
import { useLogger } from "@/composables/logger";
import DataDashboard from "@/views/DataAnalytics.vue";
import AiDosAdmin from "@/views/adminAiDos/AiDosAdmin.vue";
import AiDosOverview from "@/views/adminAiDos/AiDosOverview.vue";
import AiDosSizingParity from "@/views/adminAiDos/AiDosSizingParity.vue";
import AiDosEndpointTester from "@/views/adminAiDos/AiDosEndpointTester.vue";
import AiDosCandidates from "@/views/adminAiDos/AiDosCandidates.vue";
import AiDosRankingValidation from "@/views/adminAiDos/AiDosRankingValidation.vue";
import AiDosCalculationTester from "@/views/adminAiDos/AiDosCalculationTester.vue";
import AiDosAgentCore from "@/views/adminAiDos/AiDosAgentCore.vue";
import AiDosErpMappings from "@/views/adminAiDos/AiDosErpMappings.vue";

import { navigationGuard, LoginCallback } from "@okta/okta-vue";
import AuthProvider from "@/views/AuthProvider.vue";

// import SpyglassLite from "@/views/SpyglassLite.vue";
import { nextTick } from "vue";
import EulaAdmin from "@/components/EulaAdmin.vue";
import { useOnline } from "@vueuse/core";
import { retrieveAccessTokenFromLocalStorage } from "@/utils/utils-dependent";

const { debug } = useLogger("router");
const isOnline = useOnline();

const routes: Array<RouteRecordRaw> = [
  // {
  //   path: "/lite",
  //   component: SpyglassLite,
  //   name: "Lite"
  // },

  {
    path: "/logout",
    name: "Logout",
    component: LoggedOut
  },
  {
    path: "/",
    component: AuthProvider,
    meta: {
      requiresAuth: true
    },
    children: [
      ...sizingsRoutes,
      ...quotesRoutes,
      {
        path: "/",
        name: "Home",
        component: Home,
        meta: {
          title: "Recent Sizings",
          requiresAuth: true
        }
      },
      {
        path: "/eula",
        name: "Eula",
        component: EulaAdmin,
        meta: {
          requiresAdmin: true,
          requiresAuth: true,
          title: "Eula"
        }
      },
      {
        path: "/admin",
        name: "Admin",
        component: EquipmentAdmin,
        props: route => ({
          routerPath: route.path
        }),
        children: [
          ...EquipmentRoutes,
          {
            path: "deleted-sizings",
            name: "DeletedSizings",
            component: DeletedSizings
          },
          {
            path: "data",
            name: "DataDashboard",
            component: DataDashboard
          },
          {
            path: "ai-dos",
            name: "AiDosAdmin",
            component: AiDosAdmin,
            redirect: { name: "AiDosAdminOverview" },
            meta: {
              requiresAdmin: true,
              title: "AI DOS Admin"
            },
            children: [
              {
                path: "",
                name: "AiDosAdminOverview",
                component: AiDosOverview,
                meta: {
                  requiresAdmin: true,
                  title: "AI DOS Admin"
                }
              },
              {
                path: "sizing-parity",
                name: "AiDosSizingParity",
                component: AiDosSizingParity,
                meta: {
                  requiresAdmin: true,
                  title: "AI DOS Sizing Parity"
                }
              },
              {
                path: "material-search",
                name: "AiDosEndpointTester",
                component: AiDosEndpointTester,
                meta: {
                  requiresAdmin: true,
                  title: "AI DOS Material Search"
                }
              },
              {
                path: "erp-mappings",
                name: "AiDosErpMappings",
                component: AiDosErpMappings,
                meta: {
                  requiresAdmin: true,
                  title: "AI DOS ERP Material Mappings"
                }
              },
              {
                path: "candidates",
                name: "AiDosCandidates",
                component: AiDosCandidates,
                meta: {
                  requiresAdmin: true,
                  title: "AI DOS Verified Candidates"
                }
              },
              {
                path: "ranking-validation",
                name: "AiDosRankingValidation",
                component: AiDosRankingValidation,
                meta: {
                  requiresAdmin: true,
                  title: "AI DOS Sizing Comparison"
                }
              },
              {
                path: "calculation-tester",
                name: "AiDosCalculationTester",
                component: AiDosCalculationTester,
                meta: {
                  requiresAdmin: true,
                  title: "AI DOS Calculation Tester"
                }
              },
              {
                path: "agentcore",
                name: "AiDosAgentCore",
                component: AiDosAgentCore,
                meta: {
                  requiresAdmin: true,
                  title: "AI DOS AgentCore Admin"
                }
              }
            ]
          }
        ],
        meta: {
          requiresAdmin: true,
          requiresAuth: true,
          title: "Admin"
        }
      },
      // {
      //   path: "/legacy-importer",
      //   name: "Legacy Importer",
      //   component: LegacyImporter,
      //   meta: {
      //     requiresAdmin: true,
      //     title: "Legacy Importer",
      //     requiresAuth: true
      //   }
      // },
      {
        path: "/user",
        name: "User",
        component: User,
        meta: {
          title: "User",
          requiresAuth: true
        }
      },
      {
        path: "/wells/:well",
        name: "Well",
        component: Well,
        props: route => ({
          wellId: route.params.well
        }),
        meta: {
          title: "Well",
          requiresAuth: true
        }
      },
      {
        path: "/wells",
        name: "Wells",
        component: Wells,
        meta: {
          title: "Wells",
          requiresAuth: true
        }
      },
      {
        path: "/cache-miss",
        name: "CacheMiss",
        component: CacheMiss
      },

      {
        path: "/:pathMatch(.*)*",
        name: "NotFound",
        component: PageNotFound,
        meta: {
          title: "404"
        }
      }
    ]
  },
  {
    path: "/login/callback",
    component: LoginCallback
  }
];

function wait(duration: number) {
  return new Promise(resolve => setTimeout(resolve, duration));
}

async function tryScrollToAnchor(hash: string, timeout = 1000, delay = 100) {
  while (timeout > 0) {
    const el = document.querySelector(hash);
    if (el) {
      const { bottom } = el.getBoundingClientRect();
      if (bottom !== 0) {
        el.scrollIntoView({ behavior: "smooth" });
        break;
      }
    }
    await wait(delay);
    timeout = timeout - delay;
  }
}

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition && !to.hash) {
      // push/pop state (forward/back from browser)
      debug(
        "savedPosition found on target route",
        savedPosition,
        "scrolling to it..."
      );
      return savedPosition;
    } else if (to.hash) {
      debug("hash found on target", to.hash, "scrolling to it...");
      tryScrollToAnchor(to.hash, 5000, 50);
    }
  }
});

router.beforeEach(to => {
  // no route guards for Spyglass Lite
  if (to.name === "Lite") {
    return;
  }
  if (to.name === "Eula" && !isOnline.value) {
    router.push({ name: "Home" });
  }
  // Enforce requiresAdmin route meta at navigation time. When the access
  // token is not loaded yet, fall through to the component-level admin gate
  // in EquipmentAdmin.vue rather than blocking legitimate admins.
  if (to.matched.some(record => record.meta.requiresAdmin)) {
    const claims = retrieveAccessTokenFromLocalStorage()?.claims as
      | Record<string, unknown>
      | undefined;
    const role = claims?.["x-hasura-default-role"];
    if (typeof role === "string" && role && role !== "spyglass_admin") {
      return { name: "Home" };
    }
  }
  navigationGuard(to);
});

router.afterEach(to => {
  nextTick(() => {
    const { title } = to.meta;
    document.title = (title ? `${title} - ` : "") + "Spyglass";
  });
});

export default router;
